import { useState, useEffect, useCallback } from "react";
import { cn } from "lib/utils";

/**
 * Shared slideshow engine used by every slider on the site (homepage hero,
 * institution hero). Slides are plain objects from the CMS; the optional
 * per-slide fields below are what the admin "slide options" row edits:
 *
 *   showText          false hides the caption on that slide (image only)
 *   textFromPrevious  true reuses the previous slide's caption — the photo
 *                     changes but the text stays put (no re-animation)
 *   animation         one of ANIMATIONS[].id (default "fade")
 *   duration          seconds the slide stays on screen (default per slider)
 *
 * Transitions are CSS (see `.sl-*` in index.css): each slide renders as a
 * stacked layer that gets `sl-in` when it becomes current, `sl-out` for the
 * one it replaced and `sl-off` otherwise. `layerClass()` builds that.
 */

export const ANIMATIONS = [
  { id: "fade", label: "Cross-fade", hint: "Soft dissolve between slides" },
  { id: "slide", label: "Slide", hint: "New slide pushes in from the side" },
  { id: "slide-up", label: "Slide up", hint: "New slide rises into place" },
  { id: "zoom", label: "Zoom", hint: "New slide settles in from a close-up" },
  { id: "kenburns", label: "Ken Burns", hint: "Fade in, then drift slowly while on screen" },
  { id: "blur", label: "Blur", hint: "Blurred to sharp" },
  { id: "wipe", label: "Wipe", hint: "Revealed edge to edge like a curtain" },
  { id: "none", label: "None", hint: "Instant cut" },
];

export const DEFAULT_ANIMATION = "fade";

/**
 * Optional dark wash painted over a slide photo so overlaid text stays
 * readable. Default is "none" — the school wants photos shown clean, and a
 * shade is opt-in per slide for images that would otherwise swallow the text.
 */
export const SHADES = [
  { id: "none", label: "None (clean photo)", hint: "Photo at full strength" },
  { id: "light", label: "Light", hint: "Slight wash on the text side" },
  { id: "medium", label: "Medium", hint: "Balanced - readable over most photos" },
  { id: "strong", label: "Strong", hint: "Heavy wash for busy or pale photos" },
];

export const DEFAULT_SHADE = "none";

/**
 * How a photo fills its slide. Default is "contain": the whole picture is
 * visible and the slide background shows in any leftover band, rather than the
 * picture being cropped to fill. "cover" is the old behaviour, kept per slide
 * for photos where a crop actually looks better.
 */
export const FITS = [
  { id: "contain", label: "Show whole photo", hint: "Nothing is cut off" },
  { id: "cover", label: "Fill and crop", hint: "Fills the slider, edges may be cut off" },
];

export const DEFAULT_FIT = "contain";

export const fitClass = (fit) => (fit === "cover" ? "object-cover" : "object-contain");


const SHADE_CSS = {
  light:
    "linear-gradient(90deg, rgba(0,40,28,0.58) 0%, rgba(0,40,28,0.28) 50%, rgba(0,40,28,0.06) 100%)",
  medium:
    "linear-gradient(90deg, rgba(0,40,28,0.74) 0%, rgba(0,40,28,0.44) 48%, rgba(0,40,28,0.16) 100%)",
  strong:
    "linear-gradient(90deg, rgba(0,40,28,0.88) 0%, rgba(0,40,28,0.58) 45%, rgba(0,40,28,0.28) 100%)",
};

/** CSS background for a slide shade, or null when the photo stays clean. */
export const shadeCss = (shade) => SHADE_CSS[shade] || null;


export const normalizeAnimation = (a) =>
  ANIMATIONS.some((x) => x.id === a) ? a : DEFAULT_ANIMATION;

/** Whether this slide shows a caption at all. Absent = shown. */
export const slideShowsText = (slide) => slide?.showText !== false;

/**
 * The caption a slide should display, honouring `textFromPrevious`: walks
 * back to the nearest slide that carries its own text. `key` is that slide's
 * index — use it as the React key on the caption so consecutive slides that
 * share a caption don't re-animate it.
 */
export function resolveSlideText(slides, i) {
  let j = i;
  while (j > 0 && slides[j]?.textFromPrevious) j -= 1;
  return { text: slides[j] || {}, key: j };
}

/** Class list for slide layer `i` given the slideshow state. */
export function layerClass(i, { cur, prev }, animation, extra) {
  const state = i === cur ? "sl-in" : i === prev ? "sl-out" : "sl-off";
  return cn("sl-layer", `sl-${normalizeAnimation(animation)}`, state, extra);
}

/**
 * Auto-advancing slideshow state.
 *
 *   const show = useSlideshow(slides, { defaultDuration: 5.5 });
 *   show.cur / show.prev / show.dir, show.go(i), show.next(), show.back()
 *
 * Each slide's own `duration` (seconds) wins over `defaultDuration`.
 */
export function useSlideshow(slides, { defaultDuration = 6 } = {}) {
  const n = slides?.length || 0;
  const [state, setState] = useState({ cur: 0, prev: -1, dir: 1 });

  const go = useCallback(
    (i, dir) =>
      setState((s) => {
        if (n < 1) return s;
        const next = ((i % n) + n) % n;
        if (next === s.cur) return s;
        return { cur: next, prev: s.cur, dir: dir ?? (next > s.cur ? 1 : -1) };
      }),
    [n]
  );
  const next = useCallback(() => setState((s) => (n < 2 ? s : { cur: (s.cur + 1) % n, prev: s.cur, dir: 1 })), [n]);
  const back = useCallback(() => setState((s) => (n < 2 ? s : { cur: (s.cur - 1 + n) % n, prev: s.cur, dir: -1 })), [n]);

  // If slides shrink underneath us (admin edit / reload), stay in range.
  const cur = n ? Math.min(state.cur, n - 1) : 0;
  const prev = state.prev < n ? state.prev : -1;

  // Keyed on the current slide's duration, not the array, so callers may pass a
  // freshly built array each render without restarting the timer.
  const secs = Math.max(1.5, Number(slides?.[cur]?.duration) || defaultDuration);
  useEffect(() => {
    if (n < 2) return undefined;
    const t = setTimeout(next, secs * 1000);
    return () => clearTimeout(t);
  }, [n, cur, secs, next]);

  return { cur, prev, dir: state.dir, go, next, back };
}
